import React, { useEffect, useMemo, useState, useContext, useCallback } from 'react';
import { Accordion, Alert, Button } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useParams, useBlocker } from 'react-router-dom';
import { PageTitle } from '@project/components';
import ProfileStatus from '../../components/ProfileStatus/ProfileStatus';
import { ASSET_SECTIONS, PUBLISHED } from '../../constants';
import ProfilePublishButton from '../../containers/ProfilePublishButton/ProfilePublishButton';
import axios from '../../utils/axios';
import { get } from '../../utils/utils';
import AdminLogout from '../AdminLogout/AdminLogout';
import AssetModal from '../AssetModal/AssetModal';
import AssetSection from '../AssetSection/AssetSection';
import BasicInfoForm from '../BasicInfoForm/BasicInfoForm';
import BasicInfoContinuedForm from '../BasicInfoContinuedForm/BasicInfoContinuedForm';
import SpecialScopesForm from '../SpecialScopes/SpecialScopesForm';
import ProfileFloors from '../ProfileFloors/ProfileFloors';
import ProfileImageUpload from '../ProfileImageUpload/ProfileImageUpload';
import MenuToggle from '../Shared/MenuToggle/MenuToggle';
import spaceProfileDefaults, { setDefaults } from './defaults';
import { useSpaceProfile } from './hooks.js';
import layoutSchema from './layoutSchema';
import { useTimer } from '@project/components/src/utils/hooks/index'
import { AdminModal } from '@project/components'
import styles from './SpaceProfile.module.css'
import Axios from 'axios'
import { toast } from 'react-toastify';
import { RootContext } from '../../hoc/RootContext/RootContext';
import formSchema from '../BasicInfoForm/schema'
import { useDebounce } from '../../utils/hooks.js';
const classNames = require('classnames');

// timeout in milliseconds
const PAGE_REFRESH_TIMEOUT = process.env.REACT_APP_ID360_IDLE_TIMEOUT

function SpaceProfile(props) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const params = useParams();

  const guid = params.guid;

  let schema = formSchema.basic_info

  const [spaceProfile, setSpaceProfile] = useState(null);
  const [parentProfile, setParentProfile] = useState(); // if the current floor is not the primary, then main profile will hold it
  const [assets, setAssets] = useState(null)
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [errors, setErrors] = useState(null);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [assetSection, setAssetSection] = useState('');
  const [currentAsset, setCurrentAsset] = useState(null);
  const [openSection, setOpenSection] = useState('Basic Info');
  const [activeUploadModal, setActiveUploadModal] = useState(null);
  const [floorData, setFloorData] = useState([]);
  const [fieldSettings, setFieldSettings] = useState(null);
  const [spacePages, setSpacePages] = useState(null);
  const [generalSettings, setGeneralSettings] = useState(null);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false)
  const [displayAddress, setDisplayAddress] = useState(null);
  const [assetGuid, setAssetGuid] = useState(null);
  const [spaceProfilePhotos, setSpaceProfilePhotos] = useState({});
  const [floorErrors, setFloorErrors] = useState({}); // State to manage floorErrors
  const [spaceProfileCollection, setSpaceProfileCollection] = useState({})
  const debouncedProfile = useDebounce(spaceProfile, 10 * 1000); // 10 seconds
  const [propertyDisplayName, setPropertyDisplayName] = useState('');
  const [spaceAssignment, setSpaceAssignment] = useState(null);

  // `spaceProfile` mutates several times during the form lifecycle and,
  // because it was a dep for the `fetchPages` behavior in the `useSpaceProfile`
  // hook, it ended up triggering unnecessary GET requests to the API.
  // The only real dependencies that function has are the company and property
  // values from the `spaceProfile` object. That's it. So here below we memoize
  // those values so they only cascade the affected effects if these two object
  // keys end up changing for some reason.
  const companyNameMemoized = useMemo(() => (
    // The `spaceProfile` object can be `null` - See state initialization above.
    spaceProfile && spaceProfile.company ? spaceProfile.company : null
  ), [spaceProfile]);
  const propertyNameMemoized = useMemo(() => (
    // The `spaceProfile` object can be `null` - See state initialization above.
    spaceProfile && spaceProfile.property ? spaceProfile.property : null
  ), [spaceProfile]);

  useSpaceProfile(
    companyNameMemoized,
    guid,
    propertyNameMemoized,
    setSpaceProfile,
    setErrors,
    setAssets,
    setFloorData,
    setSpaceProfilePhotos,
    setFieldSettings,
    setSpacePages,
    setGeneralSettings,
    setDisplayAddress,
    setAssetGuid,
    setSpaceAssignment
  );

  useSpaceProfile(
    companyNameMemoized,
    spaceProfile ? spaceProfile.parent_guid : null,
    propertyNameMemoized,
    setParentProfile,
    setErrors
  );

  // React Router v6 blocker will set a blocking state if the form is dirty and the navigation has changed
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    isFormDirty && currentLocation.pathname !== nextLocation.pathname
  );

  // Listen to the blocker state change and throw an alert if the blocker state is blocked
  useEffect(() => {
    if (blocker.state === 'blocked') {
      alert('You have unsaved changes. Please save before navigating away.');
    }
  }, [blocker])


  const [propertySlugData, setPropertySlugData] = useState(null);
  const { assetSchema } = useContext(RootContext);

  /** On DebounceProfile Change */
  useEffect(() => {
    // Note: This gets called on initial load because spaceProfile is set, but since no fields are changed yet (isDirty is false) the profile doesn't actually save yet
    saveProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedProfile])

  /** On isFormDirty Change */
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isFormDirty) {
        // Note:  I could not get this custom message to display in Chrome.  It just displays the default.
        const msg = 'Changes you made may not be saved.'
        event.preventDefault();
        event.returnValue = msg; // Displaying a custom message is not supported in all browsers
        return msg; // For compatibility with older browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormDirty]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    const fetchProperty = async () => {
      try {
        const result = await axios.get(`/properties/${encodeURIComponent(companyNameMemoized)}/${encodeURIComponent(propertyNameMemoized)}`, { cancelToken: source.token });
        if (result?.status === 200) {
          setPropertySlugData(result.data.record.slug);
          setPropertyDisplayName(result.data.record.displayName || result.data.record.name);
        } else {
          setErrors(`There was an error: ${result.errors}`);
        }
      } catch (e) {
        if (!Axios.isCancel(e)) {
          throw e;
        }
      }
    };
    if (companyNameMemoized && propertyNameMemoized) {
      fetchProperty();
    }
    return () => {
      source.cancel();
    };
  }, [companyNameMemoized, propertyNameMemoized]);


  useTimer({
    ms: PAGE_REFRESH_TIMEOUT, isFormDirty, onTimeout: () => {
      setShowTimeoutModal(true)
    }
  })

  const fetchSpaceProfileCollection = useCallback(async () => {
    const profileResponses = await Promise.all(
      floorData.map(floor => axios.get(`/space-profiles/${floor.guid}`))
    );

    const spaceProfileLevels = {};
    profileResponses.forEach((response) => {
      const floor = floorData.find(floor => floor.guid === response.data.record.guid);
      if (floor) {
        spaceProfileLevels[floor.floor] = response.data.record;
      }
    });

    setSpaceProfileCollection(spaceProfileLevels)
  }, [floorData])

  useEffect(() => {
    if (floorData.length > 0) {
      fetchSpaceProfileCollection()
    }
  }, [floorData, fetchSpaceProfileCollection])

  const generateFieldSettings = useCallback((settings) => {
    // This will populate the generated field settings with the stored value, or default to true
    const initBasicInfoFieldValues = (fields, objKey) => {
      Object.keys(fields).forEach(field => {
        const fieldKey = objKey ? `${objKey}.${field}` : field;
        if (field !== null && typeof fields[field] === 'object') {
          return initBasicInfoFieldValues(fields[field], fieldKey);
        }
        fields[field] = {};

        // determine the schema based default for if the field is required to publish

        // populate each field setting with either default values, or the ones contained in the saved field settings
        ['visible', 'required', 'softRequired', 'enabled', 'surveyed', 'photosEnabled'].forEach(setting => {
          const defaultValue = get(
            schema,
            fieldKey.replace('basic_info.', '') + `.${setting}`,
            ['required', 'softRequired', 'surveyed', 'photosEnabled'].includes(setting) ? false : true
          );
          // get the setting for this or use the default value defined (or assumed) from the schema
          fields[field][setting] = get(settings, `${fieldKey}.${setting}`, defaultValue);
        });
      });
    }

    // build the basic info fields into the fields data structure
    const fields = { basic_info: JSON.parse(JSON.stringify(spaceProfileDefaults.basic_info)) };
    // this will set true or false values on the fields pertaining to the company's settings
    initBasicInfoFieldValues(fields.basic_info, 'basic_info');

    // build and init asset related fields onto the field datastructure
    const savedFieldAssets = (settings && settings.assets && Array.isArray(settings.assets)) ? settings.assets : [];
    fields.assets = Object.keys(assetSchema).map(section => {
      const savedSection = savedFieldAssets.find(_ => _.section === section) || { assets: [] };
      return {
        section,
        assets: assetSchema[section].map(asset => {
          const savedAsset = savedSection.assets.find(_ => _.type === asset.type);
          return {
            type: asset.type,
            visible: savedAsset ? savedAsset.visible : (asset.visible !== undefined ? asset.visible : true),
            fields: asset.fields.filter(_ => _.type !== 'label' && _.id !== 'coalesce_not_present_assets').map(field => {
              const savedField = savedAsset && savedAsset.fields && Array.isArray(savedAsset.fields) ? savedAsset.fields.find(_ => _.id === field.id) : null;
              return { id: field.id, visible: savedField ? savedField.visible : (field.visible !== undefined ? field.visible : true) }
            })
          }
        })
      }
    });

    return fields;
  }, [assetSchema, schema])

  const validateBasicInfoErrors = useCallback((schema, settings = null, data = {}, errors = [], softRequiredErrors = [], sectionKeys = [], sectionLabel) => {
    Object.keys(schema).filter(_ => _ !== 'label').forEach(key => {
      const fieldSchema = schema[key];
      const fieldSetting = get(settings, key);

      const value = data[key];
      if (fieldSchema.type === 'boolean') return;
      if (!fieldSchema.type && typeof fieldSchema === 'object') return validateBasicInfoErrors(fieldSchema, get(settings, key), data[key], errors, softRequiredErrors, [...sectionKeys, key], fieldSchema.label);

      // check if the field settings structure requires this input
      const isRequiredBySettings = fieldSetting && fieldSetting.required ? fieldSetting.required : false;
      // check if the field settings structure has disabled this input
      const isDisabledBySettings = fieldSetting && fieldSetting.enabled === false;
      // determine if the field is required based on field settings vs the schema
      let isRequired = fieldSetting ? isRequiredBySettings : fieldSchema.required;

      // if this profile is a tearsheet (from basic_info structure inside data), then isRequired should be overridden to false
      if (spaceProfile.basic_info?.tear_sheet) {
        isRequired = false;
      }

      const fullKey = [...sectionKeys, key].join('.');

      if (!value && value !== 0 && isRequired && !isDisabledBySettings && !data[key + '_not_surveyed']) {
        errors.push({ key: fullKey, message: intl.formatMessage({ id: 'error.required' }, { field: `${sectionLabel ? `${sectionLabel} - ` : ''}${fieldSchema.label}` }) })
      }

      // check if the field settings soft requires this field
      const isFieldSoftRequired = fieldSetting && fieldSetting.softRequired && !isRequired;

      if (!value && value !== 0 && isFieldSoftRequired) {
        softRequiredErrors.push({ key: fullKey, message: intl.formatMessage({ id: 'error.required' }, { field: `${sectionLabel ? `${sectionLabel} - ` : ''}${fieldSchema.label}` }) })
      }

      // validate that if it is a select, and a value is present, that the value(s) are within the options
      if (fieldSchema.type === 'select' && value && fieldSchema.options) {
        // split the option.value by commas, and check to see if each is in the field schema options
        const invalidValues = value
          .split(', ')
          .filter(value => !fieldSchema.options.find(option => option.value === value));

        // if any invalid values are found, add an error
        if (invalidValues?.length) {
          errors.push({
            key: fullKey,
            message: intl.formatMessage({ id: 'error.valid' }, { field: `${sectionLabel ? `${sectionLabel} - ` : ''}${fieldSchema.label} (current value contains: ${invalidValues.join(', ')})` })
          })
        }
      }
    });

    return { errors, softRequiredErrors };
  }, [intl, spaceProfile])

  useEffect(() => {
    if (!fieldSettings) {
      setFieldSettings(generateFieldSettings({}));
    }
  }, [fieldSettings, generateFieldSettings]);

  useEffect(() => {
    if (spaceProfileCollection) {
      const errorsMap = {}
      for (const profile in spaceProfileCollection) {
        const validation = validateBasicInfoErrors(formSchema, fieldSettings, spaceProfileCollection[profile]);
        const errors = validation.errors;
        const softErrors = validation.softRequiredErrors;
        const totalErrors = [...errors, ...softErrors];

        if (totalErrors.length > 0) {
          errorsMap[profile] = totalErrors
        } else {
          continue
        }
      }
      setFloorErrors(errorsMap)
    }
  }, [fieldSettings, spaceProfileCollection, validateBasicInfoErrors])

  // sends the space profile to the api to save
  const saveProfile = async (forceSave = false, onSuccess) => {
    if (!isFormDirty && !forceSave) return;
    setIsFormDirty(false);

    if (get(spaceProfile, 'basic_info.integrations.idp_record_id')) spaceProfile.idp_record_id = get(spaceProfile, 'basic_info.integrations.idp_record_id');

    try {
      // toast('Saving...', { autoClose: false, hideProgressBar: false });
      const result = await toast.promise(axios.put(`/space-profiles/${guid}`, { spaceProfile }), {
        pending: t('ui.saving') + '...',
        success: t('ui.saved'),
        error: t('ui.errorSaving')
      });

      setDefaults(spaceProfileDefaults, result.data.record);
      let record = result.data.record;
      record.basic_info = spaceProfile.basic_info;
      setSpaceProfile(record);

      // Keeps the current state of the profile collection in sync with server changes
      for (let profile in spaceProfileCollection) {
        if (spaceProfileCollection[profile].floor === record.floor) {
          setSpaceProfileCollection(prev => ({ ...prev, [profile]: record }))
        }
      }

      if (onSuccess) onSuccess();
    } catch (e) {
      setIsFormDirty(true);
      setErrors(`${t('error.general')}: ${e.errors}`);
    }
  }

  const activateAssetModal = (section, asset) => {
    setAssetSection(section);
    if (asset) setCurrentAsset(asset); // the asset for the asset card that was clicked
    setShowAssetModal(true);
  };

  if (!spaceProfile || (spaceProfile.parent_guid && !parentProfile)) return null;

  const isReadOnly = (parentProfile || spaceProfile).status === PUBLISHED;

  const SaveButton = () => {
    return (
      <div>
        <Button variant="none" className={classNames('btn-prime', 'm-1', styles.headerActionBtn, isFormDirty ? null : styles.disabled)} disabled={!isFormDirty} onClick={() => saveProfile(true)}>Save</Button>
      </div>
    )
  }

  return (
    <section>
      {showTimeoutModal &&
        <AdminModal
          title={t('SpaceProfile.modal.timeoutModalHeader')}
          show={true}
          noSave={true}
          onSave={() => { }}
          onHide={() => { }}
          preventDismiss={true}
          footer={(<Button variant="primary" onClick={() => window.location.reload()} className='btn-prime'>{t('SpaceProfile.modal.timeoutmodalReload')}</Button>)}>

          <div><p className={styles.modalMessage}>{t('SpaceProfile.modal.timeoutModalText')}</p></div>

        </AdminModal>
      }

      {/* display error if there is no asset guid */}
      {/* there is no reference to asset or space record */}
      {/* so ignoring those checks */}
      {assetGuid && assetGuid === "BAD_GUID_EXCEPTION" && <Alert variant="danger">{t('SpaceProfile.alerts.noAssetGuid')}</Alert>}

      <PageTitle title={`${spaceProfile.address2} - ${propertyDisplayName ? propertyDisplayName : propertyNameMemoized} - ${t('platform.roles.admin')} - ID360`} />
      <header className={styles.stickyHeader}>
        <div className="d-flex align-items-center w-100">
          <div className='d-flex  flex-grow-1'>
            <MenuToggle />
            <h3 className='is-page-title ml-3'>{t('SpaceProfile.title')}</h3>
          </div>
          <SaveButton />
          <div>
            <ProfilePublishButton
              individualProfile={spaceProfile}
              spaceProfile={parentProfile ? parentProfile : spaceProfile}
              setSpaceProfile={parentProfile ? setParentProfile : setSpaceProfile}
              errors={errors}
              setErrors={setErrors}
              fieldSettings={fieldSettings}
              floorErrors={floorErrors}
              setFloorErrors={setFloorErrors}
              spaceProfileCollection={spaceProfileCollection}
              validateBasicInfoErrors={validateBasicInfoErrors}
            />
          </div>
          <AdminLogout />
        </div>
      </header>
      <main>
        <ProfileStatus spaceProfile={parentProfile || spaceProfile} individualProfile={spaceProfile} generalSettings={generalSettings} />
        {errors && (errors.length || !Array.isArray(errors)) ? <Alert key={errors} variant='danger'>
          {Array.isArray(errors) ? errors.map((error, i) => <div key={`error${i}`}>{error.message}</div>) : <>{t('error')}: {errors}</>}
        </Alert> : null}
        <ProfileFloors
          spaceProfile={spaceProfile}
          floorData={floorData}
          setFloorData={setFloorData}
          errors={errors}
          setErrors={setErrors}
          saveProfile={saveProfile}
          isReadOnly={isReadOnly}
          floorErrors={floorErrors}
          setFloorErrors={setFloorErrors}
        />
        <Accordion defaultActiveKey="Basic Info" className='is-common-accordion' onSelect={setOpenSection}>
          {spaceProfile &&
            <BasicInfoForm
              layoutSchema={[layoutSchema.categories[0]]}
              spaceProfile={spaceProfile}
              saveProfile={() => { }}
              setSpaceProfile={setSpaceProfile}
              setErrors={setErrors}
              setIsFormDirty={setIsFormDirty}
              setActiveUploadModal={setActiveUploadModal}
              errors={errors}
              isReadOnly={isReadOnly}
              fieldSettings={fieldSettings}
              openSection={openSection}
              setOpenSection={setOpenSection}
              generalSettings={generalSettings}
              displayAddress={displayAddress}
              assetGuid={assetGuid}
              spaceProfilePhotos={spaceProfilePhotos}
              setSpaceProfilePhotos={setSpaceProfilePhotos}
              propertyDisplayName={propertyDisplayName}
              spaceAssignment={spaceAssignment} />}

          {spaceProfile &&
            <BasicInfoContinuedForm
              layoutSchema={[layoutSchema.categories[1]]}
              spaceProfile={spaceProfile}
              saveProfile={() => { }}
              setSpaceProfile={setSpaceProfile}
              setErrors={setErrors}
              setIsFormDirty={setIsFormDirty}
              setActiveUploadModal={setActiveUploadModal}
              errors={errors}
              isReadOnly={isReadOnly}
              fieldSettings={fieldSettings}
              openSection={openSection}
              setOpenSection={setOpenSection}
              generalSettings={generalSettings}
              spaceProfilePhotos={spaceProfilePhotos}
              setSpaceProfilePhotos={setSpaceProfilePhotos} />}

          {spaceProfile &&
            <SpecialScopesForm
              layoutSchema={[layoutSchema.categories[2]]}
              spaceProfile={spaceProfile}
              saveProfile={() => { }}
              setSpaceProfile={setSpaceProfile}
              setErrors={setErrors}
              setIsFormDirty={setIsFormDirty}
              setActiveUploadModal={setActiveUploadModal}
              errors={errors}
              isReadOnly={isReadOnly}
              fieldSettings={fieldSettings}
              openSection={openSection}
              setOpenSection={setOpenSection}
              generalSettings={generalSettings}
              spaceProfilePhotos={spaceProfilePhotos}
              setSpaceProfilePhotos={setSpaceProfilePhotos} />}

          {ASSET_SECTIONS.map(section =>
            assets &&
            <AssetSection
              key={section}
              title={section}
              assets={assets.filter(({ section: _ }) => _ === section.toLowerCase().replace(/ /g, '_')).map(asset => {
                asset.propertySlug = propertySlugData
                asset.companyDisplayName = spaceProfile.displayCompany
                return asset
              })}
              spacePages={spacePages}
              showAssetModal={activateAssetModal}
              saveProfile={() => { }}
              readOnly={isReadOnly}
              openSection={openSection}
              fieldSettings={fieldSettings?.assets} />)
          }
        </Accordion>
      </main>
      {assetSection ?
        <AssetModal
          showModal={showAssetModal}
          assetData={currentAsset}
          setShowModal={setShowAssetModal}
          assetSection={assetSection}
          spaceProfile={spaceProfile}
          isReadOnly={isReadOnly}
          setSpaceProfile={setSpaceProfile}
          setCurrentAsset={setCurrentAsset}
          assets={assets}
          setAssets={setAssets}
          fieldSettings={fieldSettings?.assets} />
        : null}
      <ProfileImageUpload
        spaceProfile={spaceProfile}
        setErrors={setErrors}
        activeUploadModal={activeUploadModal}
        setActiveUploadModal={setActiveUploadModal}
        setSpaceProfile={setSpaceProfile} />
    </section>
  )
}

export default SpaceProfile;
